import { useMemo } from 'react';
import { useToggle } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';

import { Tabs } from '@app/shared/ui/atoms/Common';
import { TabsContent } from '@app/shared/ui/molecules/Friends';
import {
  CookiesTab,
  // RankTab,
  TopBar,
  UserList,
  XPTab,
} from '@app/shared/ui/organisms/Friends';
import { useGetLeaderboardQuery, useGetMyUserQuery } from '@app/store/slices/friends/friendsApi';
import { useSearchParamsUpdater } from '@app/shared/hooks/useSearchParamsUpdater';
import { useDisplayError } from '@app/shared/hooks/useDisplayError';
import {
  isValidLeaderboardFilter,
  isValidMyUserFilter,
  getFiltersFromSortBy,
  getSortByFromFilters,
  mapLeaderboardUserToListItem,
  mapLeaderboardToUserList,
} from '@app/shared/utils/friends';
import { cn, telegramVibrate } from '@app/shared/utils/common';
import { SortBy, BalanceType } from '@app/shared/types/pages/friends';
import {
  LeaderboardTypeFilter,
  LeaderboardFilters,
  MyUserFilters,
} from '@app/store/slices/friends/friendsTypes';

export default function FriendsPage() {
  const [isMenuOpen, toggleMenu] = useToggle(false);
  const { searchParams, updateSearchParams } = useSearchParamsUpdater<LeaderboardFilters>();
  const type = searchParams.get('type');
  const isMyTeam = searchParams.get('isMyTeam') === 'true';
  const sortType = searchParams.get('sortType');
  const sortOrder = searchParams.get('sortOrder');
  const leaderboardFilters = useMemo(
    (): LeaderboardFilters => ({
      type: isValidLeaderboardFilter('type', type) ? type : LeaderboardTypeFilter.COOKIE,
      isMyTeam: isValidLeaderboardFilter('isMyTeam', isMyTeam) ? isMyTeam : false,
      sortType: isValidLeaderboardFilter('sortType', sortType) ? sortType : 'top',
      sortOrder: isValidLeaderboardFilter('sortOrder', sortOrder) ? sortOrder : 'desc',
    }),
    [type, isMyTeam, sortType, sortOrder]
  );
  const myUserFilters = useMemo(
    (): MyUserFilters => ({
      type: isValidMyUserFilter('type', leaderboardFilters.type)
        ? leaderboardFilters.type
        : undefined,
    }),
    [leaderboardFilters]
  );
  const {
    data: leaderboard,
    isLoading: isLeaderboardLoading,
    isError: isLeaderboardError,
  } = useGetLeaderboardQuery(leaderboardFilters);
  const { data: myUser, isError: isMyUserError } = useGetMyUserQuery(myUserFilters, {
    skip: !myUserFilters.type,
  });
  const { t } = useTranslation();

  useDisplayError(isLeaderboardError, t('errors.failed_to_load_leaderboard'));
  useDisplayError(isMyUserError, t('errors.failed_to_load_user'));

  const handleTabChange = (value: string) => {
    updateSearchParams({ type: value as LeaderboardFilters['type'] });
  };

  const handleIsMyTeamChange = (value: boolean) => {
    telegramVibrate();
    updateSearchParams({ isMyTeam: value });
  };

  const handleSortByChange = (value: string) => {
    telegramVibrate();

    const filters = getFiltersFromSortBy(value as SortBy);
    updateSearchParams({ sortType: filters.sortType, sortOrder: filters.sortOrder });
  };

  return (
    <Tabs
      value={leaderboardFilters.type}
      onValueChange={handleTabChange}
      className="h-full pb-[20px] pt-3"
    >
      <div className="flex h-full flex-col">
        <TopBar
          isMenuOpen={isMenuOpen}
          toggleMenu={toggleMenu}
          isTeam={leaderboardFilters.isMyTeam}
          onIsTeamChange={handleIsMyTeamChange}
          sortBy={getSortByFromFilters(leaderboardFilters.sortType, leaderboardFilters.sortOrder)}
          onSortByChange={handleSortByChange}
        />

        {/*<RankTab*/}
        {/*  users={mapLeaderboardToUserList(leaderboard?.data?.users || [], {*/}
        {/*    balanceType: BalanceType.PANDA,*/}
        {/*  })}*/}
        {/*  isUsersLoading={isLeaderboardLoading}*/}
        {/*/>*/}

        <XPTab
          users={mapLeaderboardToUserList(leaderboard?.data?.users ?? [], {
            balanceType: BalanceType.XP,
          })}
          isUsersLoading={isLeaderboardLoading}
        />

        <CookiesTab
          users={mapLeaderboardToUserList(leaderboard?.data?.users ?? [], {
            balanceType: BalanceType.COOKIE,
          })}
          isUsersLoading={isLeaderboardLoading}
          myUser={
            myUser?.data
              ? mapLeaderboardUserToListItem(
                  myUser.data.user,
                  true,
                  myUser.data.position - 1,
                  BalanceType.COOKIE
                )
              : undefined
          }
        />

        <TabsContent value={LeaderboardTypeFilter.TON}>
          <UserList
            users={mapLeaderboardToUserList(leaderboard?.data?.users ?? [], {
              balanceType: BalanceType.TON,
            })}
            isUsersLoading={isLeaderboardLoading}
            className={cn('h-full overflow-auto', 'scrollbar-hide')}
          />
        </TabsContent>

        <TabsContent value="clan">Clans</TabsContent>
      </div>
    </Tabs>
  );
}
